<template>
  <div class="drawing-board" :class="{ 'is-drawing': enabled && mode !== 'none' }">
    <canvas
      ref="canvasRef"
      class="drawing-canvas"
      :style="{ pointerEvents: enabled && mode !== 'none' ? 'auto' : 'none' }"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp"
      @pointerleave="onPointerUp"
    ></canvas>

    <div
      v-if="showToolbar"
      ref="toolbarRef"
      class="drawing-toolbar"
      :class="{ 'is-dragging': isToolbarDragging }"
      :style="toolbarStyle"
      @pointerdown.stop="onToolbarPointerDown"
    >
      <div class="drag-handle" title="拖动工具栏">
        <span></span>
        <span></span>
      </div>

      <button
        type="button"
        class="tool-btn"
        :class="{ active: mode === 'pen' }"
        title="画笔"
        @click="setMode('pen')"
      >
        <ion-icon :icon="pencilOutline"></ion-icon>
      </button>

      <div class="color-list">
        <button
          v-for="color in colorOptions"
          :key="color"
          type="button"
          class="color-dot"
          :class="{ active: mode === 'pen' && currentColor === color }"
          :style="{ background: color }"
          :title="color"
          @click="selectColor(color)"
        ></button>
      </div>

      <button
        type="button"
        class="tool-btn"
        :class="{ active: mode === 'eraser' }"
        title="橡皮擦"
        @click="setMode('eraser')"
      >
        <svg class="eraser-icon" viewBox="0 0 24 24" aria-hidden="true">
          <!-- 直立橡皮块 -->
          <rect
            x="7.2"
            y="3.5"
            width="9.6"
            height="13.2"
            rx="1.6"
            transform="rotate(18 12 10)"
            fill="currentColor"
            opacity="0.16"
          />
          <rect
            x="7.2"
            y="3.5"
            width="9.6"
            height="13.2"
            rx="1.6"
            transform="rotate(18 12 10)"
            fill="none"
            stroke="currentColor"
            stroke-width="1.7"
          />
          <!-- 顶部纸套分界 -->
          <path
            d="M6.6 8.8l8.4 2.8"
            fill="none"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
          />
          <!-- 底部擦痕 -->
          <path
            d="M4 20.2h11.5"
            fill="none"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
          />
          <path
            d="M16.2 20.2h3.3"
            fill="none"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
            opacity="0.45"
          />
        </svg>
      </button>

      <button type="button" class="tool-btn danger" title="全部清除" @click="handleClear">
        <ion-icon :icon="trashOutline"></ion-icon>
      </button>
    </div>
  </div>
</template>

<script>
import { IonIcon } from "@ionic/vue"
import { pencilOutline, trashOutline } from "ionicons/icons"

const DEFAULT_COLORS = ["#E53935", "#1E88E5", "#43A047", "#FDD835", "#000000", "#FFFFFF"]

export default {
  name: "DrawingBoard",
  components: {
    IonIcon,
  },
  props: {
    /** 是否启用绘制（关闭后画布不拦截事件） */
    enabled: {
      type: Boolean,
      default: true,
    },
    /** 是否显示工具栏 */
    showToolbar: {
      type: Boolean,
      default: true,
    },
    /** 可选颜色 */
    colors: {
      type: Array,
      default: () => [...DEFAULT_COLORS],
    },
    /** 默认画笔颜色 */
    defaultColor: {
      type: String,
      default: "#E53935",
    },
    /** 画笔粗细 */
    penWidth: {
      type: Number,
      default: 4,
    },
    /** 橡皮擦粗细 */
    eraserWidth: {
      type: Number,
      default: 24,
    },
    /** 初始模式：pen | eraser | none */
    defaultMode: {
      type: String,
      default: "pen",
      validator: (v) => ["pen", "eraser", "none"].includes(v),
    },
    /** 清除前是否二次确认（由父级处理时可关掉） */
    confirmClear: {
      type: Boolean,
      default: true,
    },
  },
  emits: [
    "update:mode",
    "update:color",
    "clear",
    "draw-start",
    "draw-end",
    "change",
    "toolbar-move",
  ],
  data() {
    return {
      pencilOutline,
      trashOutline,
      mode: this.defaultMode,
      currentColor: this.defaultColor,
      isDrawing: false,
      lastPoint: null,
      ctx: null,
      resizeObserver: null,
      dpr: 1,
      /** 工具栏位置（null 表示使用默认底部居中） */
      toolbarX: null,
      toolbarY: null,
      isToolbarDragging: false,
      toolbarDragMoved: false,
      toolbarDragOffset: { x: 0, y: 0 },
      toolbarPointerId: null,
    }
  },
  computed: {
    colorOptions() {
      return this.colors?.length ? this.colors : DEFAULT_COLORS
    },
    toolbarStyle() {
      if (this.toolbarX === null || this.toolbarY === null) {
        return {
          left: "50%",
          bottom: "calc(12px + env(safe-area-inset-bottom, 0px))",
          top: "auto",
          transform: "translateX(-50%)",
        }
      }
      return {
        left: `${this.toolbarX}px`,
        top: `${this.toolbarY}px`,
        bottom: "auto",
        transform: "none",
      }
    },
  },
  watch: {
    enabled(val) {
      if (!val) {
        this.isDrawing = false
        this.lastPoint = null
      }
    },
    defaultColor(val) {
      if (val) this.currentColor = val
    },
    defaultMode(val) {
      if (val) this.mode = val
    },
  },
  mounted() {
    this.initCanvas()
    this.setupResizeObserver()
    window.addEventListener("resize", this.handleResize)
    window.addEventListener("pointermove", this.onToolbarPointerMove)
    window.addEventListener("pointerup", this.onToolbarPointerUp)
    window.addEventListener("pointercancel", this.onToolbarPointerUp)
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.handleResize)
    window.removeEventListener("pointermove", this.onToolbarPointerMove)
    window.removeEventListener("pointerup", this.onToolbarPointerUp)
    window.removeEventListener("pointercancel", this.onToolbarPointerUp)
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
  },
  methods: {
    initCanvas() {
      const canvas = this.$refs.canvasRef
      if (!canvas) return

      this.ctx = canvas.getContext("2d")
      this.syncCanvasSize(true)
    },

    setupResizeObserver() {
      const el = this.$el
      if (!el || typeof ResizeObserver === "undefined") return

      this.resizeObserver = new ResizeObserver(() => {
        this.syncCanvasSize()
      })
      this.resizeObserver.observe(el)
    },

    handleResize() {
      this.syncCanvasSize()
      this.clampToolbarPosition()
    },

    /**
     * 工具栏拖拽：从拖动手柄或工具栏空白处开始；按钮上短按仍可点击
     */
    onToolbarPointerDown(event) {
      // 仅左键 / 触控
      if (event.pointerType === "mouse" && event.button !== 0) return

      const toolbar = this.$refs.toolbarRef
      if (!toolbar) return

      const isHandle = event.target.closest?.(".drag-handle")
      const isControl = event.target.closest?.("button, .color-dot, .color-list")

      // 点在按钮上时不立刻抢拖拽，等移动超过阈值再拖
      // 点在手柄上则直接进入拖拽
      const rect = toolbar.getBoundingClientRect()

      // 若尚未自定义过位置，先把当前视觉位置固化为 left/top
      if (this.toolbarX === null || this.toolbarY === null) {
        this.toolbarX = rect.left
        this.toolbarY = rect.top
      }

      this.isToolbarDragging = !!isHandle
      this.toolbarDragMoved = false
      this.toolbarPointerId = event.pointerId
      this.toolbarDragOffset = {
        x: event.clientX - this.toolbarX,
        y: event.clientY - this.toolbarY,
      }

      if (isHandle) {
        event.preventDefault()
        toolbar.setPointerCapture?.(event.pointerId)
      } else if (!isControl) {
        // 空白区域也可拖
        event.preventDefault()
        this.isToolbarDragging = true
        toolbar.setPointerCapture?.(event.pointerId)
      }
      // 点在按钮上：不 capture，允许 click；若后续移动超阈值再转为拖拽
      else {
        this._pendingToolbarDrag = true
      }
    },

    onToolbarPointerMove(event) {
      if (this.toolbarPointerId !== event.pointerId) return

      const toolbar = this.$refs.toolbarRef
      if (!toolbar) return

      const startX = this.toolbarX + this.toolbarDragOffset.x
      const startY = this.toolbarY + this.toolbarDragOffset.y
      const delta = Math.hypot(event.clientX - startX, event.clientY - startY)

      // 从按钮按下后移动超过阈值，转为拖拽并阻止误触点击
      if (!this.isToolbarDragging && this._pendingToolbarDrag) {
        if (delta < 8) return
        this.isToolbarDragging = true
        this.toolbarDragMoved = true
        toolbar.setPointerCapture?.(event.pointerId)
      }

      if (!this.isToolbarDragging) return

      event.preventDefault()
      this.toolbarDragMoved = true

      const nextX = event.clientX - this.toolbarDragOffset.x
      const nextY = event.clientY - this.toolbarDragOffset.y
      const clamped = this.getClampedToolbarPos(nextX, nextY)
      this.toolbarX = clamped.x
      this.toolbarY = clamped.y
    },

    onToolbarPointerUp(event) {
      if (this.toolbarPointerId !== event.pointerId) return

      const toolbar = this.$refs.toolbarRef
      try {
        toolbar?.releasePointerCapture?.(event.pointerId)
      } catch (_) {
        /* ignore */
      }

      if (this.isToolbarDragging && this.toolbarDragMoved) {
        this.$emit("toolbar-move", { x: this.toolbarX, y: this.toolbarY })
        // 拖拽结束后短暂屏蔽 click，避免误触工具按钮
        this._blockToolbarClickUntil = Date.now() + 200
      }

      this.isToolbarDragging = false
      this.toolbarDragMoved = false
      this.toolbarPointerId = null
      this._pendingToolbarDrag = false
    },

    getClampedToolbarPos(x, y) {
      const toolbar = this.$refs.toolbarRef
      const margin = 8
      const width = toolbar?.offsetWidth || 200
      const height = toolbar?.offsetHeight || 52
      const maxX = Math.max(margin, window.innerWidth - width - margin)
      const maxY = Math.max(margin, window.innerHeight - height - margin)
      return {
        x: Math.min(Math.max(x, margin), maxX),
        y: Math.min(Math.max(y, margin), maxY),
      }
    },

    clampToolbarPosition() {
      if (this.toolbarX === null || this.toolbarY === null) return
      const clamped = this.getClampedToolbarPos(this.toolbarX, this.toolbarY)
      this.toolbarX = clamped.x
      this.toolbarY = clamped.y
    },

    /**
     * 同步画布像素尺寸与显示尺寸，保留已有笔迹
     */
    syncCanvasSize(force = false) {
      const canvas = this.$refs.canvasRef
      const board = this.$el
      if (!canvas || !board || !this.ctx) return

      const width = board.clientWidth
      const height = board.clientHeight
      if (width <= 0 || height <= 0) return

      const dpr = window.devicePixelRatio || 1
      const nextW = Math.round(width * dpr)
      const nextH = Math.round(height * dpr)

      if (!force && canvas.width === nextW && canvas.height === nextH) return

      let snapshot = null
      if (canvas.width > 0 && canvas.height > 0) {
        snapshot = document.createElement("canvas")
        snapshot.width = canvas.width
        snapshot.height = canvas.height
        snapshot.getContext("2d").drawImage(canvas, 0, 0)
      }

      canvas.width = nextW
      canvas.height = nextH
      canvas.style.width = `${width}px`
      canvas.style.height = `${height}px`
      this.dpr = dpr

      this.ctx.setTransform(1, 0, 0, 1, 0, 0)
      this.ctx.scale(dpr, dpr)
      this.ctx.lineCap = "round"
      this.ctx.lineJoin = "round"

      if (snapshot) {
        this.ctx.drawImage(snapshot, 0, 0, width, height)
      }
    },

    setMode(mode) {
      if (this._blockToolbarClickUntil && Date.now() < this._blockToolbarClickUntil) return
      // 再次点击同一工具则退出绘制，方便操作下方课件
      this.mode = this.mode === mode ? "none" : mode
      this.$emit("update:mode", this.mode)
    },

    selectColor(color) {
      if (this._blockToolbarClickUntil && Date.now() < this._blockToolbarClickUntil) return
      this.currentColor = color
      this.mode = "pen"
      this.$emit("update:color", color)
      this.$emit("update:mode", "pen")
    },

    getPoint(event) {
      const canvas = this.$refs.canvasRef
      const rect = canvas.getBoundingClientRect()
      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      }
    },

    applyStrokeStyle() {
      if (!this.ctx) return
      if (this.mode === "eraser") {
        this.ctx.globalCompositeOperation = "destination-out"
        this.ctx.strokeStyle = "rgba(0,0,0,1)"
        this.ctx.lineWidth = this.eraserWidth
      } else {
        this.ctx.globalCompositeOperation = "source-over"
        this.ctx.strokeStyle = this.currentColor
        this.ctx.lineWidth = this.penWidth
      }
    },

    onPointerDown(event) {
      if (!this.enabled || this.mode === "none" || !this.ctx) return

      event.preventDefault()
      const canvas = this.$refs.canvasRef
      canvas.setPointerCapture?.(event.pointerId)

      this.isDrawing = true
      this.lastPoint = this.getPoint(event)
      this.applyStrokeStyle()
      this.$emit("draw-start", { mode: this.mode, color: this.currentColor })

      // 点击也能留下点
      this.ctx.beginPath()
      this.ctx.arc(
        this.lastPoint.x,
        this.lastPoint.y,
        (this.mode === "eraser" ? this.eraserWidth : this.penWidth) / 2,
        0,
        Math.PI * 2
      )
      this.ctx.fillStyle = this.mode === "eraser" ? "rgba(0,0,0,1)" : this.currentColor
      if (this.mode === "eraser") {
        this.ctx.globalCompositeOperation = "destination-out"
      }
      this.ctx.fill()
      this.applyStrokeStyle()
    },

    onPointerMove(event) {
      if (!this.isDrawing || !this.lastPoint || !this.ctx) return
      event.preventDefault()

      const point = this.getPoint(event)
      this.ctx.beginPath()
      this.ctx.moveTo(this.lastPoint.x, this.lastPoint.y)
      this.ctx.lineTo(point.x, point.y)
      this.ctx.stroke()
      this.lastPoint = point
    },

    onPointerUp(event) {
      if (!this.isDrawing) return
      this.isDrawing = false
      this.lastPoint = null
      this.$emit("draw-end")
      this.$emit("change")
      try {
        this.$refs.canvasRef?.releasePointerCapture?.(event.pointerId)
      } catch (_) {
        /* ignore */
      }
    },

    handleClear() {
      if (this._blockToolbarClickUntil && Date.now() < this._blockToolbarClickUntil) return
      this.clear()
    },

    /** 清除全部笔迹（可供父组件调用） */
    clear(options = {}) {
      const canvas = this.$refs.canvasRef
      if (!canvas || !this.ctx) return

      this.ctx.setTransform(1, 0, 0, 1, 0, 0)
      this.ctx.clearRect(0, 0, canvas.width, canvas.height)
      this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0)
      this.ctx.lineCap = "round"
      this.ctx.lineJoin = "round"

      if (!options.silent) {
        this.$emit("clear")
        this.$emit("change")
      }
    },

    /** 导出当前笔迹为 dataURL */
    toDataURL(type = "image/png", quality) {
      return this.$refs.canvasRef?.toDataURL(type, quality) || ""
    },

    /** 从 dataURL 恢复笔迹（可供父组件调用） */
    loadDataURL(dataUrl) {
      this.clear({ silent: true })
      if (!dataUrl) return Promise.resolve()

      return new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => {
          const canvas = this.$refs.canvasRef
          if (!canvas || !this.ctx) {
            resolve()
            return
          }
          this.ctx.drawImage(img, 0, 0, canvas.clientWidth, canvas.clientHeight)
          resolve()
        }
        img.onerror = reject
        img.src = dataUrl
      })
    },

    /** 是否有笔迹（粗略检测非透明像素） */
    hasContent() {
      const canvas = this.$refs.canvasRef
      if (!canvas || !this.ctx) return false
      const { data } = this.ctx.getImageData(0, 0, canvas.width, canvas.height)
      for (let i = 3; i < data.length; i += 4) {
        if (data[i] !== 0) return true
      }
      return false
    },
  },
}
</script>

<style scoped>
.drawing-board {
  position: absolute;
  inset: 0;
  z-index: 10;
  touch-action: none;
  pointer-events: none;
}

.drawing-board.is-drawing {
  pointer-events: auto;
}

.drawing-canvas {
  width: 100%;
  height: 100%;
  display: block;
  touch-action: none;
  cursor: crosshair;
}

.drawing-toolbar {
  position: fixed;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px 8px 8px;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid #e5e5e5;
  border-radius: 999px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  pointer-events: auto;
  z-index: 1000;
  max-width: calc(100vw - 24px);
  overflow-x: auto;
  touch-action: none;
  user-select: none;
  cursor: grab;
}

.drawing-toolbar.is-dragging {
  cursor: grabbing;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  opacity: 0.96;
}

.drag-handle {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 3px;
  width: 18px;
  height: 36px;
  padding: 0 4px;
  flex-shrink: 0;
  cursor: grab;
}

.drag-handle span {
  display: block;
  width: 100%;
  height: 2px;
  border-radius: 1px;
  background: #c0c0c0;
}

.drawing-toolbar.is-dragging .drag-handle {
  cursor: grabbing;
}

.tool-btn {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: #f3f3f3;
  color: #333;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  cursor: pointer;
  flex-shrink: 0;
  touch-action: manipulation;
  transition:
    background 0.15s ease,
    color 0.15s ease;
}

.eraser-icon {
  width: 20px;
  height: 20px;
  display: block;
}

.tool-btn.active {
  background: #1e88e5;
  color: #fff;
}

.tool-btn.danger {
  background: #ffebee;
  color: #e53935;
}

.tool-btn.danger:active {
  background: #ffcdd2;
}

.color-list {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 4px;
}

.color-dot {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid transparent;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
}

.color-dot.active {
  border-color: #1e88e5;
  box-shadow:
    0 0 0 2px rgba(30, 136, 229, 0.25),
    inset 0 0 0 1px rgba(0, 0, 0, 0.1);
}
</style>
